import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, Shield, Layers, Thermometer } from "lucide-react";
import axios from "axios";
import { SEO, buildProductJsonLd } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";
import { HeroImage } from "@/components/HeroImage";
import { EnquiryForm } from "@/components/EnquiryForm";
import { BuyNowButton } from "@/components/BuyNowButton";
import { AddToCartButton } from "@/components/AddToCartButton";
import { ProductOptions } from "@/components/ProductOptions";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { RecentlyViewedStrip } from "@/components/RecentlyViewedStrip";
import { ProductGallery, normalizeGalleryImage } from "@/components/ProductGallery";
import { WhyItsDifferentSection } from "@/components/WhyItsDifferentSection";
import { VideoLightbox } from "@/components/VideoLightbox";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n.toLocaleString("en-NZ");

export default function RoofTentDetailPage() {
  const { slug } = useParams();
  const [models, setModels] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/rooftent`).then((res) => setModels(res.data)).catch((err) => { console.error(err); setModels([]); });
  }, []);

  const model = models?.find((m) => m.slug === slug);
  const hasOptions = model?.optionGroups?.length > 0;
  const [optionsState, setOptionsState] = useState({ selectedOptions: {}, totalPrice: model?.price || 0, allRequiredSelected: !hasOptions, combinationInStock: true });
  const recentlyViewed = useRecentlyViewed(
    model && { path: `/roof-tents/${model.slug}`, name: model.name, thumb: model.thumb, price: model.price, priceFrom: model.priceFrom, priceOnRequest: model.priceOnRequest }
  );

  if (models === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }
  if (!model) return <Navigate to="/roof-tents" replace />;

  const related = models
    .filter((m) => m.type === model.type && m.slug !== model.slug)
    .slice(0, 3);

  const gallery = model.gallery && model.gallery.length > 0 ? model.gallery.map(normalizeGalleryImage) : [{ src: model.thumb, alt: null, type: "image", pos: "center" }];
  const canBuyNow = !model.priceOnRequest && model.inStock !== false;

  return (
    <div data-testid="roof-tent-detail-page">
      <SEO
        path={`/roof-tents/${model.slug}`}
        title={`${model.name} · NZ`}
        description={`${model.name} — ${model.tagline}. ${model.intro.slice(0, 140)}`}
        image={model.hero}
        jsonLd={buildProductJsonLd({ name: model.name, description: model.tagline, image: model.hero, price: model.price, priceOnRequest: model.priceOnRequest, inStock: model.inStock, url: `/roof-tents/${model.slug}` })}
      />

      {/* HERO */}
      <section className="relative min-h-[70svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage src={model.hero} alt={model.name} className="w-full h-full" desktopPosition={model.heroPosition || "50% center"} mobilePosition={model.heroFocalMobile} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-8% via-black/40 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-16 pt-32 w-full">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <Link to="/roof-tents" data-testid="detail-breadcrumb" className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 hover:text-[var(--brand-primary)] transition-colors">
              ← Roof Tents
            </Link>
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/80 bg-black/60 backdrop-blur-sm px-2.5 py-1">
              {model.type === "hard-shell" ? "HARD SHELL" : "SOFT SHELL"}
            </span>
            {model.badge && (
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
                {model.badge}
              </span>
            )}
          </div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.9] text-white hero-text-shadow max-w-4xl">
            {model.slug === "quick-pitch-hard-shell" ? (
              <>GENERIC SHELL WITH<br />PLASTIC CORNERS?<br /><span className="text-[var(--brand-primary)]">NOT AT</span> $5K+.</>
            ) : (
              model.name.toUpperCase()
            )}
          </h1>
          <p className="text-base md:text-lg text-[var(--brand-primary)] font-mono uppercase tracking-widest mt-5">
            {model.slug === "quick-pitch-hard-shell" ? "Quick Pitch Hard Shell RTT" : model.tagline}
          </p>
          {model.slug === "quick-pitch-hard-shell" && (
            <p className="text-base md:text-lg text-white/85 leading-relaxed mt-4 max-w-xl">
              A premium price should buy a genuinely premium build. This one's an original South African design, proven globally.
            </p>
          )}
          <div className="mt-8 flex items-start gap-6 flex-wrap">
            <div>
              {model.priceWas && (
                <p className="font-mono text-sm text-white/50 line-through">${fmt(model.priceWas)}</p>
              )}
              <p className="font-display text-4xl md:text-5xl text-white leading-none">
                {model.priceOnRequest ? (
                  <span className="text-2xl font-mono uppercase tracking-widest text-white/80">Enquire For Price</span>
                ) : (
                  <>
                    {(model.priceFrom && !hasOptions) && <span className="text-lg font-mono uppercase tracking-widest text-white/70 mr-2">FROM</span>}
                    ${fmt(hasOptions ? optionsState.totalPrice : model.price)}
                  </>
                )}
              </p>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 mt-2">
                {model.priceOnRequest ? "" : "GST included · "}{model.availability}
              </p>
            </div>
            <div className="flex items-start gap-3 flex-wrap">
              {/* "Enquire" leads here too — same evidence as Wellside: 0 tents
                  sold via self-checkout in 30 days site-wide, only small
                  accessories convert that way at this price point. */}
              <a href="#enquire" data-testid="detail-cta-enquire" className="btn-primary">
                Enquire About This <ArrowUpRight size={14} />
              </a>
              {canBuyNow && (
                <>
                  <BuyNowButton
                    productPath={`/roof-tents/${model.slug}`}
                    testId="detail-cta-buy-now"
                    selectedOptions={hasOptions ? optionsState.selectedOptions : null}
                    disabled={hasOptions && (!optionsState.allRequiredSelected || !optionsState.combinationInStock)}
                    disabledHint={hasOptions && optionsState.allRequiredSelected && !optionsState.combinationInStock ? "" : "Select all options below to continue"}
                  />
                  <AddToCartButton
                    productPath={`/roof-tents/${model.slug}`}
                    testId="detail-cta-add-to-cart"
                    name={model.name}
                    thumb={model.thumb}
                    unitPrice={hasOptions ? optionsState.totalPrice : model.price}
                    priceFrom={model.priceFrom && !hasOptions}
                    selectedOptions={hasOptions ? optionsState.selectedOptions : null}
                    disabled={hasOptions && (!optionsState.allRequiredSelected || !optionsState.combinationInStock)}
                  />
                </>
              )}
            </div>
          </div>
          {/* Compact trust strip — right next to the price, above the fold.
              Scoped to Quick Pitch Hard Shell only: analytics showed THIS
              specific model's page (not the others) gets heavy cold paid
              social traffic landing directly here (not via the category
              page, which already explains this stuff first) — median dwell
              ~7s, ~12% scroll depth, i.e. most never scroll far enough to
              see WHY it costs what it does. Same 3 themes as the category
              page's "what to actually look for" checklist, just compressed
              to fit right where the price is. */}
          {model.slug === "quick-pitch-hard-shell" && (
            <div className="flex flex-wrap gap-2 mt-5" data-testid="detail-hero-trust-strip">
              <div className="inline-flex items-center gap-2 border border-white/20 bg-black/30 px-3 py-1.5">
                <Shield size={13} className="text-[var(--brand-primary)]" strokeWidth={1.8} />
                <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/80">Welded Shell. No Plastic Corners.</span>
              </div>
              <div className="inline-flex items-center gap-2 border border-white/20 bg-black/30 px-3 py-1.5">
                <Layers size={13} className="text-[var(--brand-primary)]" strokeWidth={1.8} />
                <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/80">Up To 800gsm Genuine SA Canvas</span>
              </div>
              <div className="inline-flex items-center gap-2 border border-white/20 bg-black/30 px-3 py-1.5">
                <Thermometer size={13} className="text-[var(--brand-primary)]" strokeWidth={1.8} />
                <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/80">Real Foam Insulation, Not A Mat</span>
              </div>
            </div>
          )}
          {hasOptions && (
            <div className="mt-8 max-w-lg">
              <ProductOptions optionGroups={model.optionGroups} variantStock={model.variantStock || []} basePrice={model.price} onChange={setOptionsState} />
            </div>
          )}
        </div>
      </section>

      {/* GALLERY + INTRO */}
      <section className="container-x py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <ProductGallery gallery={gallery} productName={model.name} />
          </div>

          {/* Intro + Features */}
          <div className="lg:col-span-5 space-y-8">
            <div>
              <p className="overline mb-4">— About This Tent</p>
              <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed whitespace-pre-line">{model.intro}</p>
            </div>
            <div className="border-t border-[var(--border-default)] pt-8">
              <p className="overline mb-5">— Key Features</p>
              <ul className="space-y-3">
                {model.features.map((f, i) => (
                  <li key={f || i} className="flex items-start gap-3 text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                    <Check size={16} className="text-[var(--brand-primary)] mt-1 shrink-0" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* VIDEO — Quick Pitch RTT only. Sits right after the gallery/intro,
          before the text-heavy "Why It's Different" + specs sections, so
          the visual content (photos -> video) stays together. */}
      {model.slug === "quick-pitch-hard-shell" && (
        <section className="container-x pb-16 md:pb-20" data-testid="quick-pitch-video-section">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <p className="overline mb-4">— See It In Action</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
              THE QUICK PITCH RTT, <span className="text-[var(--brand-primary)]">DEMONSTRATED.</span>
            </h2>
          </div>
          <div className="max-w-4xl mx-auto relative aspect-video overflow-hidden border border-[var(--border-default)] bg-black">
            <VideoLightbox
              videoId="AdNOCvuu2zM"
              title="Quick Pitch RTT — demonstrated"
              testId="quick-pitch-video"
              caption="Watch The Walkaround"
            />
          </div>
        </section>
      )}

      {/* WHY IT'S DIFFERENT */}
      <WhyItsDifferentSection contentSections={model.contentSections} />

      {/* SPECS */}
      <section className="border-y border-[var(--border-default)] bg-[var(--bg-secondary)]">
        <div className="container-x py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="overline mb-4">— Specifications</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                THE <span className="text-[var(--brand-primary)]">NUMBERS.</span>
              </h2>
              <p className="text-[var(--text-secondary)] mt-5 leading-relaxed">
                Note: some brands quote weights excluding mattress and ladder. All figures below are like-for-like, real-world.
              </p>
            </div>
            <div className="lg:col-span-8">
              <dl className="divide-y divide-[var(--border-default)] border-t border-b border-[var(--border-default)]">
                {model.specs.map(([k, v], i) => (
                  <div key={k || i} data-testid={`spec-row-${i}`} className="grid grid-cols-3 gap-4 py-4">
                    <dt className="font-mono text-xs uppercase tracking-wider text-[var(--brand-primary)] col-span-1">{k}</dt>
                    <dd className="text-base md:text-lg text-white col-span-2">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="container-x py-16 md:py-20">
          <div className="mb-8">
            <p className="overline mb-3">— You Might Also Consider</p>
            <h2 className="font-display text-3xl md:text-4xl text-white">
              MORE <span className="text-[var(--brand-primary)]">{model.type === "hard-shell" ? "HARD SHELLS" : "SOFT SHELLS"}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((r, i) => (
              <Link
                key={r.slug}
                to={`/roof-tents/${r.slug}`}
                data-testid={`related-tent-${i}`}
                className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <HoverImage primary={r.thumb} secondary={r.gallery?.[1]?.src} alt={r.name} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-display text-xl md:text-2xl text-white leading-tight">{r.name}</h3>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-[var(--brand-primary)] mt-2">
                      {r.priceFrom ? "FROM " : ""}${fmt(r.price)}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <RecentlyViewedStrip items={recentlyViewed} />

      {/* ENQUIRY */}
      <section id="enquire" className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)] scroll-mt-24">
        <div className="container-x py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="overline mb-4">— Enquire About The {model.name.split(" ").slice(0, 3).join(" ")}</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                ASK US<br />
                <span className="text-[var(--brand-primary)]">ANYTHING.</span>
              </h2>
              <p className="text-[var(--text-secondary)] mt-6 leading-relaxed">
                Availability, dimensions for your specific vehicle, roof-load compatibility, mounting brackets — get a proper answer from someone who lives this stuff.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="border border-[var(--border-default)] bg-[var(--bg-main)] p-6 md:p-8">
                <EnquiryForm defaultProduct={`Roof Tent — ${model.name}`} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
